import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { CARD_BACKS, type CardBack } from '~/store/cardBacks.js';
import { PlayingCard } from '~/table/PlayingCard.js';
import { Button } from './Button.js';
import { FieldLabel } from './FieldLabel.js';

/**
 * The visual setting for a card back.
 *
 * One card shows the real art the table renders, and it is the biggest thing
 * in the control, because the art is what is being chosen. Previous and next
 * wrap around the finite deck, while the live name and position say where the
 * player is. The group owns its accessible name so a pair of arrow buttons
 * never becomes two anonymous controls beside an unexplained card.
 *
 * The arrows flank the card alone, and the caption sits under the row rather
 * than inside it. The first draft stacked the name into the row's middle
 * column, which made that column as wide as whichever name was showing — so
 * the arrows slid in and out on every flip. Now the row is three fixed-size
 * things, and each caption line keeps to a single truncating line, so nothing
 * a name does can move anything.
 *
 * The arrows are `ghost` buttons: bare ink chevrons, no chip. The chip
 * version came first, and once the card grew to hero size the two stroked
 * circles crowded it — a stroked card between two stroked chips reads as
 * three chips arguing. The ghost keeps the chip's geometry, so the 48px
 * target, the hover wash and the press nudge all survive; only the face, the
 * stroke and the shadow go. The chevron is Phosphor's `CaretLeft`/`CaretRight`
 * in a 16px square, which replaced the hand-drawn 2-point SVG when the icon set
 * came in — the font's own arrow glyph is a wide display character that shouted
 * over the card.
 *
 * The card sets its width through PlayingCard; `.card-shape` keeps the
 * corner proportional to the rendered box.
 */
export function CardBackPicker({
  value,
  onChange,
  tag,
}: {
  readonly value: CardBack;
  readonly onChange: (value: CardBack) => void;
  /** A mark pinned to the card's corner, such as In use or Members. Positions itself. */
  readonly tag?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const labelId = useId();
  const currentIndex = Math.max(
    0,
    CARD_BACKS.findIndex((cardBack) => cardBack.id === value),
  );
  const current = CARD_BACKS[currentIndex] ?? CARD_BACKS[0];

  const move = (offset: number): void => {
    const nextIndex = (currentIndex + offset + CARD_BACKS.length) % CARD_BACKS.length;
    const next = CARD_BACKS[nextIndex];
    if (next !== undefined) onChange(next.id);
  };

  return (
    <div className="flex flex-col gap-3 py-2 text-ink" role="group" aria-labelledby={labelId}>
      <FieldLabel id={labelId} hint={t('settings.cardBackHint')}>
        {t('settings.cardBack')}
      </FieldLabel>

      <div className="mx-auto flex w-full max-w-80 items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.cardBackPrevious')}
          onClick={() => {
            move(-1);
          }}
        >
          <CaretLeftIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>

        <div className="relative">
          <PlayingCard
            size="picker-preview"
            card={null}
            cardBack={current.id}
            flightId="card-back-preview"
          />
          {tag}
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.cardBackNext')}
          onClick={() => {
            move(1);
          }}
        >
          <CaretRightIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>
      </div>

      <div className="text-center" aria-live="polite">
        <p className="truncate text-sm leading-snug text-ink">
          {t(`settings.cardBacks.${current.id}`)}
        </p>
        <p className="truncate text-sm text-gray-700">
          {t('settings.cardBackPosition', {
            current: currentIndex + 1,
            total: CARD_BACKS.length,
          })}
        </p>
      </div>
    </div>
  );
}
